import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Tag, Input, Space, Typography, Card,
  Avatar, Tooltip, Badge, Statistic, Row, Col,
} from 'antd';
import {
  SearchOutlined, UserOutlined, CrownOutlined,
  CheckCircleOutlined, CloseCircleOutlined,
  SafetyOutlined, BankOutlined, TeamOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { theme as antdTheme } from 'antd';
import axios from 'axios';

const { Title, Text } = Typography;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const AdminUsers = () => {
  const { token: tk } = antdTheme.useToken();
  const navigate      = useNavigate();

  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const res = await axios.get('/api/admin/users', { headers: authHeader(), params });
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  /* ── Derived stats ── */
  const total      = users.length;
  const verified   = users.filter(u => u.email_verified).length;
  const superAdmins = users.filter(u => u.is_super_admin).length;
  const proUsers   = users.filter(u => u.org_plan === 'pro').length;

  /* ── Helpers ── */
  const initials = (u) => {
    if (u.name) return u.name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
    return u.email[0].toUpperCase();
  };

  const avatarColor = (id) => {
    const palette = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981','#3b82f6','#ef4444','#14b8a6'];
    return palette[id % palette.length];
  };

  /* ── Columns ── */
  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, r) => (
        <Space>
          <Avatar
            size={36}
            style={{ background: avatarColor(r.id), flexShrink: 0, fontWeight: 600 }}
          >
            {initials(r)}
          </Avatar>
          <Space direction="vertical" size={0}>
            <Space size={6}>
              <Text strong style={{ fontSize: 13 }}>{r.name || '—'}</Text>
              {r.is_super_admin && (
                <Tooltip title="Super Admin">
                  <SafetyOutlined style={{ color: '#f59e0b', fontSize: 13 }} />
                </Tooltip>
              )}
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>{r.email}</Text>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Organisation',
      key: 'org',
      render: (_, r) => (
        <Space
          size={6}
          style={{ cursor: r.org_id ? 'pointer' : 'default' }}
          onClick={() => r.org_id && navigate(`/admin/orgs/${r.org_id}`)}
        >
          <BankOutlined style={{ color: tk.colorTextSecondary }} />
          <Text style={{ fontSize: 13, color: r.org_id ? tk.colorPrimary : undefined }}>
            {r.org_name}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Plan',
      dataIndex: 'org_plan',
      key: 'plan',
      width: 100,
      render: (plan) =>
        plan === 'pro' ? (
          <Tag icon={<CrownOutlined />} color="gold">Pro</Tag>
        ) : plan === 'free' ? (
          <Tag color="default">Free</Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 100,
      render: (role, r) =>
        r.is_super_admin ? (
          <Tag color="volcano">Super Admin</Tag>
        ) : (
          <Tag color="blue" style={{ textTransform: 'capitalize' }}>{role}</Tag>
        ),
    },
    {
      title: 'Verified',
      dataIndex: 'email_verified',
      key: 'verified',
      width: 100,
      align: 'center',
      render: (v) =>
        v ? (
          <Tooltip title="Email verified">
            <CheckCircleOutlined style={{ color: '#10b981', fontSize: 16 }} />
          </Tooltip>
        ) : (
          <Tooltip title="Not verified">
            <CloseCircleOutlined style={{ color: tk.colorTextQuaternary, fontSize: 16 }} />
          </Tooltip>
        ),
    },
    {
      title: 'Joined',
      dataIndex: 'created_at',
      key: 'joined',
      width: 140,
      render: (d) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
        </Text>
      ),
    },
  ];

  return (
    <div style={{ padding: '0 2px' }}>
      {/* ── Header ── */}
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ marginBottom: 4 }}>All Users</Title>
        <Text type="secondary">Every registered user across all organisations.</Text>
      </div>

      {/* ── Stats row ── */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {[
          { title: 'Total Users',    value: total,      icon: <TeamOutlined />,         color: tk.colorPrimary },
          { title: 'Verified',       value: verified,   icon: <CheckCircleOutlined />,  color: '#10b981' },
          { title: 'Pro Plan',       value: proUsers,   icon: <CrownOutlined />,        color: '#f59e0b' },
          { title: 'Super Admins',   value: superAdmins,icon: <SafetyOutlined />,       color: '#ef4444' },
        ].map(s => (
          <Col xs={12} sm={6} key={s.title}>
            <Card
              size="small"
              style={{
                borderRadius: 10,
                border: `1px solid ${tk.colorBorderSecondary}`,
                background: tk.colorBgContainer,
              }}
            >
              <Statistic
                title={<Text type="secondary" style={{ fontSize: 12 }}>{s.title}</Text>}
                value={s.value}
                valueStyle={{ color: s.color, fontSize: 22, fontWeight: 700 }}
                prefix={React.cloneElement(s.icon, { style: { fontSize: 16, marginRight: 4, color: s.color } })}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* ── Table card ── */}
      <Card
        style={{
          borderRadius: 12,
          border: `1px solid ${tk.colorBorderSecondary}`,
          background: tk.colorBgContainer,
        }}
        styles={{ body: { padding: 0 } }}
      >
        {/* Toolbar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: `1px solid ${tk.colorBorderSecondary}`,
          flexWrap: 'wrap', gap: 8,
        }}>
          <Space>
            <Badge count={total} color={tk.colorPrimary} overflowCount={9999}>
              <Text strong style={{ fontSize: 14 }}>Users</Text>
            </Badge>
          </Space>
          <Input
            placeholder="Search name or email…"
            prefix={<SearchOutlined style={{ color: tk.colorTextQuaternary }} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            allowClear
            style={{ width: 260, borderRadius: 8 }}
          />
        </div>

        <Table
          rowKey="id"
          dataSource={users}
          columns={columns}
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: false,
            showTotal: (n) => `${n} users`,
            style: { padding: '12px 20px', margin: 0 },
          }}
          style={{ borderRadius: '0 0 12px 12px', overflow: 'hidden' }}
          onRow={(r) => ({
            style: { cursor: 'default' },
          })}
        scroll={{ x: 'max-content' }}
        />
      </Card>
    </div>
  );
};

export default AdminUsers;
